import { useEffect, useRef, useState } from 'react';
import type { DemoKey, DemoScript, FingerKey, Fx, Level, Pt, RoundProps, ViewProps } from '../kit.js';
import { BOARD, BoardFrame, HIT_MS, INK, OK, STROKE, TYPE, alpha, clamp, fingerAt, mono, q, say, seeded, thirtyGame, useRound, useSvgId } from '../kit.js';
import { ERA_SKIN } from '../../art.js';
import type { Era } from '../../art.js';

/**
 * SIGNAL OFFICER · AIM THE DISH — a satellite drifts across the sky; point the dish's beam at it
 * and keep it there until the signal locks.
 *
 * In place of PASS IT ON (Marek, 6 Oct 2026: "it's not working" — showing a code to a guest across
 * the table), a game played alone like every other. The dish stands at the foot of the board and
 * its beam points wherever the finger is: touch the sky anywhere and drag, and the beam swings to
 * follow, so the finger can hold below the satellite without hiding it. While a finger is down and
 * the satellite is inside the beam, the ring round the satellite and the lock bar on the ground
 * fill; off it they drain slowly, so a slip costs little. Full, the signal locks: a line joins the
 * dish to the satellite, and the next one comes up on the other side of the sky. Two satellites a
 * round, one at a time. Round 1 drifts slowly in a straight line; round 2 a touch faster; round 3 a
 * touch faster again on a gentle arc. There is nothing to get wrong. The sky and the dish follow
 * the era: stars over the ship's hull, a neon skyline, smoke and drifting wreckage, dust over the
 * dunes, and the treetops at dusk.
 *
 * A converted board (kit.tsx): the rules as plain functions, a small view state (`DishView`), a
 * pure `View`, the `Board` that plays it, and a `demo` played by the rules themselves.
 */

export interface DishLevel {
  /** Satellites to lock this round, one after the other. */
  sats: number;
  /** How fast a satellite drifts across the sky, board units a second. */
  speed: number;
  /** How steeply its path climbs or falls, at most (units down per unit across). */
  slope: number;
  /** How far its path bows down at the edges of the sky: 0 straight, more a gentle arc. */
  bend: number;
  /** Half the beam's width, degrees. */
  beam: number;
  /** How long the beam must stay on a satellite to lock it, ms. */
  holdMs: number;
}

/** New on 6 Oct 2026, and easy from the start: slow and straight, a touch faster, then a touch faster on a gentle arc. */
export const DISH_LEVELS: readonly [DishLevel, DishLevel, DishLevel] = [
  { sats: 2, speed: 16, slope: 0.08, bend: 0, beam: 12, holdMs: 1_500 },
  { sats: 2, speed: 21, slope: 0.12, bend: 0, beam: 11, holdMs: 1_600 },
  { sats: 2, speed: 26, slope: 0.06, bend: 34, beam: 10, holdMs: 1_700 },
];

/** A satellite this many degrees outside the beam still counts as in it. */
export const GRACE_DEG = 3;
/** Off the satellite the lock drains at half the speed it builds. */
export const DRAIN = 0.5;
/** Once a satellite locks, the link shows this long before the next one comes up. */
export const LINK_MS = 900;
/** A new satellite fades in over this. */
const FADE_MS = 400;
/** The dish turns on this point, in board units. */
export const PIVOT: Pt = { x: 160, y: 258 };
/** The beam swings at most this far either side of straight up. */
export const MAX_AIM = 78;
/** A satellite drifts this far past one edge of the sky and comes back at the other. */
export const EDGE = 24;
/** The top of the ground the dish stands on. */
const GROUND = 284;

const DEG = 180 / Math.PI;

/** The beam's aim, degrees from straight up (− left, + right), to point at `p`: as far over as it goes for a point beside the dish. */
export function aimAt(p: Pt): number {
  return clamp(Math.atan2(p.x - PIVOT.x, Math.max(10, PIVOT.y - p.y)) * DEG, -MAX_AIM, MAX_AIM);
}

/** Which way a point in the sky lies from the dish, degrees from straight up. */
export function bearing(x: number, y: number): number {
  return Math.atan2(x - PIVOT.x, PIVOT.y - y) * DEG;
}

/** Is the satellite at (x, y) inside a beam aimed at `aim`, `beam` degrees either side? */
export function onBeam(aim: number, x: number, y: number, beam: number): boolean {
  return Math.abs(aim - bearing(x, y)) <= beam + GRACE_DEG;
}

/** One frame of the lock: it builds while the beam holds the satellite and drains slowly off it. */
export function stepLock(hold: number, dtMs: number, on: boolean): number {
  return on ? hold + dtMs : Math.max(0, hold - dtMs * DRAIN);
}

/** One satellite's way across the sky. */
export interface Track {
  /** Where it comes up, across. */
  x0: number;
  /** Its height over the middle of the sky. */
  y0: number;
  /** 1 drifting right, −1 left. */
  dir: 1 | -1;
  speed: number;
  slope: number;
  bend: number;
}

/** Where a satellite is `ageS` seconds after it came up. Past one edge it comes back at the other. */
export function satAt(tr: Track, ageS: number): Pt {
  const span = BOARD.w + 2 * EDGE;
  const run = tr.x0 + EDGE + tr.dir * tr.speed * ageS;
  const x = (((run % span) + span) % span) - EDGE;
  const u = (x - BOARD.w / 2) / (BOARD.w / 2);
  return { x, y: tr.y0 + tr.slope * (x - BOARD.w / 2) + tr.bend * u * u };
}

/**
 * A satellite for `level`, coming up on the side of the sky away from the beam (`aim`; either side
 * for the first, with the beam straight up) and drifting in towards the middle.
 */
export function dealTrack(level: Level, rand: () => number, aim: number | null): Track {
  const L = DISH_LEVELS[level];
  const right = aim === null ? rand() < 0.5 : aim <= 0;
  const slope = L.slope * (0.6 + rand() * 0.4) * (rand() < 0.5 ? -1 : 1);
  return { x0: right ? 252 + rand() * 16 : 52 + rand() * 16, y0: 86 + rand() * 26, dir: right ? -1 : 1, speed: L.speed, slope, bend: L.bend };
}

/** Everything the board shows. */
export interface DishView {
  /** The game clock, ms: the stars, the drift and the beam's pulses move with it. */
  t: number;
  /** The beam's aim, degrees from straight up (− left, + right). */
  a: number;
  /** Half the beam's width, degrees. */
  w: number;
  /** The satellite, in board units, and how far it has faded in (0–1). */
  x: number;
  y: number;
  o: number;
  /** A finger is on the board: the lock builds only then. */
  d: boolean;
  /** The lock on this satellite, 0–1. */
  h: number;
  /** Satellites locked this round, and how many to lock. */
  n: number;
  of: number;
  /** 0 not yet touched, 1 aiming, 2 just locked (the link shows), 3 every satellite locked. */
  s: 0 | 1 | 2 | 3;
  fx?: Fx | undefined;
}

interface Look {
  /** The dish's face, its back, and the mount. */
  face: string;
  back: string;
  stand: string;
  /** The ground it stands on. */
  ground: string;
  /** The satellite: its body, its panels and its trim. */
  body: string;
  panel: string;
  trim: string;
}

const LOOK: Record<Era, Look> = {
  stars: { face: '#e8eef5', back: '#7d8da0', stand: '#4f5c6c', ground: '#18212e', body: '#d9b25a', panel: '#2f5f9e', trim: '#e8f1fb' },
  neon: { face: '#3a2458', back: '#1a0d2c', stand: '#2a1840', ground: '#120820', body: '#e9e4f2', panel: '#3b1e63', trim: '#4fe0ff' },
  debris: { face: '#a9a3a0', back: '#4a3636', stand: '#3a2626', ground: '#1a0a0b', body: '#8d8f94', panel: '#3a2a2c', trim: '#ff8a3c' },
  dust: { face: '#e6cfa2', back: '#8a6a3c', stand: '#5c4424', ground: '#2e1c0b', body: '#c9a978', panel: '#5b4630', trim: '#ffd8a0' },
  canopy: { face: '#e9e6da', back: '#8c8a7a', stand: '#4f4a3e', ground: '#0c1f0c', body: '#b8b2a0', panel: '#46564e', trim: '#d6f5a8' },
};

/** Fixed points for the skies, the same on every render: stars, wreckage and dust. */
const STARS = (() => {
  const r = seeded(9_311);
  return Array.from({ length: 28 }, () => ({ x: q(6 + r() * 308), y: q(6 + r() * 220), r: q(0.6 + r() * 1.1, 1), p: q(2.6 + r() * 2.4, 1), ph: q(r() * 6.28, 2) }));
})();
const SHARDS = (() => {
  const r = seeded(5_077);
  return Array.from({ length: 6 }, () => ({ x: r() * 360, y: q(36 + r() * 170), s: q(3 + r() * 3, 1), v: q(4 + r() * 5, 1), spin: q(10 + r() * 20) }));
})();
const MOTES = (() => {
  const r = seeded(3_301);
  return Array.from({ length: 18 }, () => ({ x: r() * 340, y: q(20 + r() * 240), r: q(0.8 + r() * 1, 1), v: q(8 + r() * 10, 1), ph: q(r() * 6.28, 2) }));
})();

/** The horizons, worked out once. */
const SKYLINE = (() => {
  let d = '';
  for (let x = -6, i = 0; x < BOARD.w; x += 26, i++) {
    const h = 26 + ((i * 37) % 46);
    d += `M${x} ${GROUND} L${x} ${GROUND - h} L${x + 22} ${GROUND - h} L${x + 22} ${GROUND} Z `;
  }
  return d;
})();
const RUINS = (() => {
  let d = `M0 ${GROUND}`;
  for (let x = 0, i = 0; x <= BOARD.w; x += 16, i++) {
    const h = 18 + ((i * 29) % 24) + (i % 5 === 2 ? 22 : 0);
    d += ` L${x} ${GROUND - h} L${x + 8} ${GROUND - h + 6 + ((i * 7) % 8)}`;
  }
  return `${d} L${BOARD.w} ${GROUND} Z`;
})();
const DUNES = `M0 ${GROUND - 18} Q 60 ${GROUND - 44} 130 ${GROUND - 24} T 260 ${GROUND - 28} T 390 ${GROUND - 20} L${BOARD.w} ${GROUND} L0 ${GROUND} Z`;
const CROWNS = Array.from({ length: 15 }, (_, i) => ({ x: -6 + i * 24, y: GROUND - 12 - ((i * 11) % 16), r: 16 + ((i * 7) % 9) }));

/** The sky behind the satellite, by era, drawn from the clock alone. Nothing in it flashes. */
function Sky({ era, t, id }: { era: Era; t: number; id: string }) {
  const skin = ERA_SKIN[era];
  const twinkle = (s: (typeof STARS)[number]) => 0.3 + 0.35 * (0.5 + 0.5 * Math.sin((t * 2 * Math.PI) / s.p + s.ph));
  const stars = (count: number, dim = 1) =>
    STARS.slice(0, count).map((s, i) => <circle key={i} cx={s.x} cy={s.y} r={s.r} fill={skin.haze} opacity={twinkle(s) * dim} />);
  if (era === 'stars') {
    return (
      <g>
        <path d={`M-20 120 Q 160 40 340 150`} fill="none" stroke={skin.haze} strokeOpacity=".06" strokeWidth="40" />
        {stars(28)}
        {/* Earth's limb behind the ship */}
        <circle cx="40" cy="660" r="420" fill="#0a2448" stroke={skin.haze} strokeOpacity=".16" strokeWidth="9" />
        <circle cx="40" cy="660" r="420" fill="none" stroke={skin.haze} strokeOpacity=".5" strokeWidth="1.4" />
      </g>
    );
  }
  if (era === 'neon') {
    return (
      <g>
        {stars(9, 0.7)}
        <rect x="0" y="150" width={BOARD.w} height={GROUND - 150} fill={`url(#${id}glow)`} />
        <path d={SKYLINE} fill="#170b28" stroke={skin.ink} strokeOpacity=".3" />
        <path d={SKYLINE} fill={`url(#${id}win)`} opacity=".8" />
      </g>
    );
  }
  if (era === 'debris') {
    return (
      <g>
        {stars(6, 0.6)}
        {[0, 1].map((k) => {
          const cx = ((70 + k * 200 + t * 4) % 440) - 60;
          return <ellipse key={k} cx={cx} cy={150 + k * 40} rx="110" ry="34" fill={`url(#${id}smoke)`} />;
        })}
        {SHARDS.map((s, i) => {
          const x = ((((s.x - t * s.v) % 360) + 360) % 360) - 20;
          return (
            <path
              key={i}
              d={`M${-s.s} 0 L0 ${-s.s * 0.6} L${s.s * 0.8} ${s.s * 0.2} L${-s.s * 0.2} ${s.s * 0.7} Z`}
              transform={`translate(${x.toFixed(1)} ${s.y}) rotate(${((t * s.spin) % 360).toFixed(1)})`}
              fill="#2a1416"
              stroke={skin.haze}
              strokeOpacity=".3"
            />
          );
        })}
        <path d={RUINS} fill="#1d0a0b" stroke={skin.haze} strokeOpacity=".3" />
      </g>
    );
  }
  if (era === 'dust') {
    return (
      <g>
        <rect x="0" y="120" width={BOARD.w} height={GROUND - 120} fill={`url(#${id}glow)`} />
        {MOTES.map((m, i) => {
          const x = ((m.x + t * m.v) % 340) - 10;
          return <circle key={i} cx={x.toFixed(1)} cy={(m.y + 4 * Math.sin(t * 0.7 + m.ph)).toFixed(1)} r={m.r} fill={skin.haze} opacity=".35" />;
        })}
        <path d={DUNES} fill="#3a2410" opacity=".9" />
      </g>
    );
  }
  return (
    <g>
      {stars(14, 0.8)}
      <circle cx="44" cy="38" r="22" fill={`url(#${id}moon)`} />
      <circle cx="44" cy="38" r="10" fill="#f3f0dc" opacity=".8" />
      {CROWNS.map((c, i) => (
        <circle key={i} cx={c.x} cy={c.y} r={c.r} fill="#0c2410" stroke={skin.ink} strokeOpacity=".25" />
      ))}
    </g>
  );
}

/** The satellite, about 56 across, round (0, 0). The old one at the last stop has a bent panel. */
function Satellite({ era, tilt }: { era: Era; tilt: number }) {
  const c = LOOK[era];
  return (
    <g transform={`rotate(${tilt.toFixed(1)})`}>
      <line x1="-27" y1="0" x2="27" y2="0" stroke={c.trim} strokeWidth="1.6" />
      {([-1, 1] as const).map((side) => {
        const x = side < 0 ? -28 : 10;
        return (
          <g key={side} transform={era === 'canopy' && side === 1 ? 'rotate(14 10 0)' : undefined}>
            <rect x={x} y="-7" width="18" height="14" rx="1.5" fill={c.panel} stroke={c.trim} strokeWidth="1" />
            <path d={`M${x + 6} -7 V7 M${x + 12} -7 V7 M${x} 0 H${x + 18}`} stroke={c.trim} strokeOpacity=".45" strokeWidth=".8" />
          </g>
        );
      })}
      <rect x="-8" y="-9" width="16" height="18" rx="2.5" fill={c.body} stroke="#0b1220" strokeOpacity=".6" />
      <rect x="-6" y="-7" width="4.5" height="14" rx="1.5" fill="#ffffff" opacity=".2" />
      <path d="M-5 -9 Q0 -15 5 -9" fill="none" stroke={c.trim} strokeWidth="1.4" />
      <line x1="0" y1="-12" x2="0" y2="-17" stroke={c.trim} strokeWidth="1.2" />
      <circle cx="0" cy="-18" r="1.6" fill={c.trim} />
    </g>
  );
}

/** The ring round the satellite: a target until it fills. */
const RING_R = 34;
/** The beam, long enough to leave the board at any aim. */
const BEAM_LEN = 440;
/** The pulses along the beam: where they start, and how far apart. */
const PULSE_FROM = 50;
const PULSE_GAP = 56;
/** The lock bar on the ground. */
const BAR = { x: 206, y: 297, w: 96, h: 12 } as const;

/** The sky, the dish and the satellite, from the view state alone. */
function View({ view, era, accent, lang, input }: ViewProps<DishView>) {
  const skin = ERA_SKIN[era];
  const look = LOOK[era];
  const id = useSvgId();
  const t = view.t / 1000;
  // A demo glides every number between its keys: states and counts are read rounded.
  const state = Math.round(view.s);
  const n = Math.round(view.n);
  const linked = state >= 2;
  const h = linked ? 1 : clamp(view.h, 0, 1);
  const inBeam = onBeam(view.a, view.x, view.y, view.w);
  const on = !linked && view.d && inBeam;
  // The lock builds only with a finger down: say so if the satellite sits in the beam without one.
  const fingerDown = say(lang, 'KEEP YOUR FINGER DOWN', 'HOLD FINGEREN NEDE');
  const status =
    state === 3
      ? { text: say(lang, '✓ SIGNAL LOCKED', '✓ SIGNALET ER LÅST'), tone: 'ok' as const }
      : state === 2
        ? { text: say(lang, '✓ LOCKED', '✓ LÅST'), count: [n, view.of] as const, tone: 'ok' as const }
        : on
          ? { text: say(lang, 'ON IT — HOLD', 'PÅ DEN – HOLD'), tone: 'ok' as const, hint: say(lang, 'UNTIL IT LOCKS', 'TIL DEN LÅSES') }
          : h > 0.02
            ? { text: say(lang, 'FOLLOW IT', 'FØLG DEN'), tone: 'warn' as const, hint: view.d ? say(lang, 'BEAM BACK ON IT', 'STRÅLEN PÅ IGJEN') : fingerDown }
            : { text: say(lang, 'LOCKED', 'LÅST'), count: [n, view.of] as const, hint: inBeam && state === 1 ? fingerDown : say(lang, 'DRAG THE BEAM ONTO IT', 'DRA STRÅLEN OPP PÅ DEN') };

  const { x: px, y: py } = PIVOT;
  const half = (view.w * Math.PI) / 180;
  const spread = BEAM_LEN * Math.tan(half);
  const rad = (view.a * Math.PI) / 180;
  const feed = { x: px + 40 * Math.sin(rad), y: py - 40 * Math.cos(rad) };
  const dist = Math.hypot(view.x - px, view.y - py);
  // Pulses run out along the beam while it searches, and down it to the dish while it hears the satellite.
  const shift = (t * 60) % PULSE_GAP;
  const reach = on ? dist - 10 : 400;
  const pulses: { r: number; o: number }[] = [];
  for (let r = PULSE_FROM + (on ? PULSE_GAP - shift : shift); r < reach; r += PULSE_GAP) {
    const fadeIn = Math.min(1, (r - PULSE_FROM) / 36);
    pulses.push({ r, o: (on ? 0.75 : 0.45 * (1 - r / 400)) * fadeIn });
  }
  const ringLen = 2 * Math.PI * RING_R;
  const ringColour = linked ? OK : accent;
  const tilt = 7 * Math.sin(t * 0.8);
  const sat = { x: view.x, y: view.y };

  return (
    <BoardFrame era={era} accent={accent} status={status} fx={view.fx} input={input}>
      <defs>
        <linearGradient id={`${id}beam`} x1="0" y1="1" x2="0" y2="0">
          <stop offset="0" stopColor={accent} stopOpacity={on ? 0.5 : 0.36} />
          <stop offset=".55" stopColor={accent} stopOpacity={on ? 0.2 : 0.12} />
          <stop offset="1" stopColor={accent} stopOpacity="0" />
        </linearGradient>
        <linearGradient id={`${id}glow`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor={era === 'dust' ? skin.haze : skin.ink} stopOpacity="0" />
          <stop offset="1" stopColor={era === 'dust' ? skin.haze : skin.ink} stopOpacity={era === 'dust' ? 0.16 : 0.2} />
        </linearGradient>
        <radialGradient id={`${id}smoke`}>
          <stop offset="0" stopColor="#3a1214" stopOpacity=".7" />
          <stop offset="1" stopColor="#3a1214" stopOpacity="0" />
        </radialGradient>
        <radialGradient id={`${id}moon`}>
          <stop offset="0" stopColor="#f3f0dc" stopOpacity=".35" />
          <stop offset="1" stopColor="#f3f0dc" stopOpacity="0" />
        </radialGradient>
        <radialGradient id={`${id}halo`}>
          <stop offset="0" stopColor={ringColour} stopOpacity=".3" />
          <stop offset="1" stopColor={ringColour} stopOpacity="0" />
        </radialGradient>
        <pattern id={`${id}win`} width="7" height="8" patternUnits="userSpaceOnUse">
          <rect x="2" y="2" width="2.4" height="2.4" fill={skin.haze} opacity=".5" />
        </pattern>
      </defs>

      <Sky era={era} t={t} id={id} />

      {/* the beam: a cone from the dish, its pulses, and a faint line down its middle */}
      <g transform={`rotate(${view.a.toFixed(1)} ${px} ${py})`}>
        <path d={`M${px} ${py} L${px - spread} ${py - BEAM_LEN} L${px + spread} ${py - BEAM_LEN} Z`} fill={`url(#${id}beam)`} />
        <path
          d={`M${px - spread} ${py - BEAM_LEN} L${px} ${py} L${px + spread} ${py - BEAM_LEN}`}
          fill="none"
          stroke={accent}
          strokeOpacity={on ? 0.85 : 0.55}
          strokeWidth={on ? STROKE.line : 1.5}
        />
        <line x1={px} y1={py - 44} x2={px} y2={py - BEAM_LEN} stroke={accent} strokeOpacity=".22" strokeDasharray="3 7" />
        {pulses.map((p, i) => {
          const dx = p.r * Math.sin(half);
          const dy = p.r * Math.cos(half);
          return (
            <path
              key={i}
              d={`M${(px - dx).toFixed(1)} ${(py - dy).toFixed(1)} A ${p.r.toFixed(1)} ${p.r.toFixed(1)} 0 0 1 ${(px + dx).toFixed(1)} ${(py - dy).toFixed(1)}`}
              fill="none"
              stroke={on ? INK.hi : accent}
              strokeWidth={STROKE.line}
              strokeLinecap="round"
              opacity={p.o.toFixed(2)}
            />
          );
        })}
      </g>

      {/* once locked, a line joins the dish to the satellite, its signal running down it */}
      {linked && (
        <line
          x1={feed.x}
          y1={feed.y}
          x2={sat.x}
          y2={sat.y}
          stroke={OK}
          strokeWidth={STROKE.bold}
          strokeLinecap="round"
          strokeDasharray="10 8"
          strokeDashoffset={(-t * 40).toFixed(1)}
          opacity={view.o}
        />
      )}

      {/* the satellite, its target ring and the lock filling round it */}
      <g transform={`translate(${sat.x.toFixed(1)} ${sat.y.toFixed(1)})`} opacity={clamp(view.o, 0, 1)}>
        {(on || linked) && <circle r={RING_R + 8} fill={`url(#${id}halo)`} />}
        <circle r={RING_R} fill="none" stroke={ringColour} strokeOpacity={on || linked ? 0.35 : 0.5} strokeWidth={STROKE.line} strokeDasharray={on || linked ? undefined : '4 6'} />
        {h > 0 && (
          <circle
            r={RING_R}
            fill="none"
            stroke={ringColour}
            strokeWidth={STROKE.bold + 1}
            strokeLinecap="round"
            strokeDasharray={`${(h * ringLen).toFixed(1)} ${ringLen.toFixed(1)}`}
            transform="rotate(-90)"
          />
        )}
        <Satellite era={era} tilt={tilt} />
      </g>

      {/* the ground, and the dish on its mount */}
      <rect x="0" y={GROUND} width={BOARD.w} height={BOARD.h - GROUND} fill={look.ground} />
      <line x1="0" y1={GROUND} x2={BOARD.w} y2={GROUND} stroke={era === 'neon' ? skin.ink : skin.haze} strokeOpacity={era === 'neon' ? 0.7 : 0.3} strokeWidth={era === 'neon' ? STROKE.line : STROKE.hair} />
      {era === 'stars' && <path d="M40 284 V320 M100 284 V320 M220 284 V320 M280 284 V320" stroke={skin.haze} strokeOpacity=".12" />}
      {era === 'dust' && <rect x={px - 32} y={GROUND - 6} width="64" height="14" rx="3" fill="#4a4038" stroke="#000" strokeOpacity=".4" />}
      {era === 'canopy' &&
        [18, 52, 92, 238, 274, 304].map((x) => <path key={x} d={`M${x} ${GROUND + 6} l-4 -10 M${x} ${GROUND + 6} l1 -12 M${x} ${GROUND + 6} l5 -9`} stroke="#4f8a3e" strokeWidth="1.4" />)}
      <path d={`M${px - 7} ${py + 2} L${px - 15} ${GROUND + 8} L${px + 15} ${GROUND + 8} L${px + 7} ${py + 2} Z`} fill={look.stand} stroke="#000" strokeOpacity=".4" />
      <g transform={`rotate(${view.a.toFixed(1)} ${px} ${py})`}>
        <path d={`M${px - 30} ${py - 12} L${px} ${py - 40} L${px + 30} ${py - 12}`} fill="none" stroke={look.stand} strokeWidth="2.2" />
        <path d={`M${px - 40} ${py - 12} Q ${px} ${py + 24} ${px + 40} ${py - 12} Z`} fill={look.back} stroke="#000" strokeOpacity=".45" />
        <ellipse cx={px} cy={py - 12} rx="40" ry="8" fill={look.face} stroke={era === 'neon' ? skin.ink : '#0b1220'} strokeOpacity={era === 'neon' ? 1 : 0.5} strokeWidth={era === 'neon' ? 1.6 : 1} />
        <ellipse cx={px - 10} cy={py - 13} rx="18" ry="3" fill="#ffffff" opacity=".22" />
        {era === 'debris' && <path d={`M${px + 14} ${py - 6} l6 6 l-3 5`} stroke="#000" strokeOpacity=".5" fill="none" />}
        {era === 'canopy' && <ellipse cx={px - 26} cy={py - 3} rx="6" ry="2.6" fill="#6fb85a" transform={`rotate(-30 ${px - 26} ${py - 3})`} />}
        <rect x={px - 4} y={py - 47} width="8" height="9" rx="2" fill={on || linked ? (linked ? OK : accent) : look.stand} stroke="#000" strokeOpacity=".4" />
      </g>
      <circle cx={px} cy={py} r="5" fill={look.stand} stroke="#000" strokeOpacity=".5" />

      {/* the lock bar on the ground */}
      <text x={BAR.x - 8} y={BAR.y + BAR.h - 2} textAnchor="end" fontSize={TYPE.hint} fontWeight="700" letterSpacing="1" fontFamily={mono} fill={INK.dim}>
        {say(lang, 'LOCK', 'LÅS')}
      </text>
      <rect x={BAR.x} y={BAR.y} width={BAR.w} height={BAR.h} rx={BAR.h / 2} fill={alpha(INK.hi, 0.1)} stroke={alpha(accent, 0.35)} strokeWidth={STROKE.hair} />
      {h > 0 && <rect x={BAR.x} y={BAR.y} width={Math.max(BAR.h, BAR.w * h)} height={BAR.h} rx={BAR.h / 2} fill={linked ? OK : accent} />}
    </BoardFrame>
  );
}

function Board(props: RoundProps) {
  const { level, lang } = props;
  const L = DISH_LEVELS[level];
  const [rand] = useState(() => seeded(Date.now() + level * 7_919));
  const [first] = useState(() => dealTrack(level, rand, null));
  const track = useRef(first);
  /** Seconds since this satellite came up, on the game clock. */
  const age = useRef(0);
  const aim = useRef(0);
  const holding = useRef(false);
  const touched = useRef(false);
  const hold = useRef(0);
  const locked = useRef(0);
  /** While a lock's link shows: until when, on the game clock. */
  const linkUntil = useRef<number | null>(null);
  const best = useRef(0);

  const round = useRound(props, (dt, now, won) => {
    age.current += dt;
    if (won) return;
    if (linkUntil.current !== null) {
      if (now < linkUntil.current) return;
      linkUntil.current = null;
      track.current = dealTrack(level, rand, aim.current);
      age.current = 0;
      hold.current = 0;
      return;
    }
    const sat = satAt(track.current, age.current);
    hold.current = stepLock(hold.current, dt * 1000, holding.current && onBeam(aim.current, sat.x, sat.y, L.beam));
    const progress = Math.min(0.99, (locked.current + Math.min(1, hold.current / L.holdMs)) / L.sats);
    if (progress > best.current + 0.02) {
      best.current = progress;
      round.progress(progress);
    }
    if (hold.current >= L.holdMs) {
      locked.current += 1;
      round.hit();
      if (locked.current >= L.sats) round.win();
      else linkUntil.current = now + LINK_MS;
    }
  });

  useEffect(() => {
    if (props.paused) holding.current = false;
  }, [props.paused]);

  const sat = satAt(track.current, age.current);
  const linking = linkUntil.current !== null;
  const view: DishView = {
    t: q(round.now),
    a: q(aim.current, 1),
    w: L.beam,
    x: q(sat.x, 1),
    y: q(sat.y, 1),
    o: q(Math.min(1, (age.current * 1000) / FADE_MS), 2),
    d: holding.current,
    h: round.won || linking ? 1 : q(Math.min(1, hold.current / L.holdMs), 2),
    n: locked.current,
    of: L.sats,
    s: round.won ? 3 : linking ? 2 : touched.current ? 1 : 0,
    fx: round.fx,
  };
  const point = (p: Pt) => {
    aim.current = aimAt(p);
  };
  return (
    <View
      view={round.show(view)}
      era={props.era}
      accent={props.accent}
      lang={lang}
      input={{
        label: say(lang, 'Drag to aim the dish', 'Dra for å sikte parabolen'),
        down: (p) => {
          if (!round.live) return;
          holding.current = true;
          touched.current = true;
          point(p);
        },
        move: (p) => {
          if (holding.current && round.live) point(p);
        },
        up: () => {
          holding.current = false;
        },
      }}
    />
  );
}

/** The how-to's two satellites: one coming up on the left, then one on the right. */
const DEMO_ONE: Track = { x0: 60, y0: 104, dir: 1, speed: DISH_LEVELS[0].speed, slope: 0.05, bend: 0 };
const DEMO_TWO: Track = { x0: 262, y0: 98, dir: -1, speed: DISH_LEVELS[0].speed, slope: -0.06, bend: 0 };
/** The demo's finger holds this far out from the dish towards the satellite, so it never hides it. */
const DEMO_REACH = 0.58;
const DEMO_MS = 7_400;

/** Where the demo's finger holds to point at a satellite at `p`. */
const towards = (p: Pt): Pt => ({ x: q(PIVOT.x + (p.x - PIVOT.x) * DEMO_REACH, 1), y: q(PIVOT.y + (p.y - PIVOT.y) * DEMO_REACH, 1) });

/** The finger following a satellite, a key every 400 ms from `from` to before `to`. */
function follow(tr: Track, born: number, from: number, to: number): FingerKey[] {
  const keys: FingerKey[] = [];
  for (let at = Math.round(from); at < to; at += 400) keys.push({ at, ...towards(satAt(tr, (at - born) / 1000)), ease: 'linear' });
  return keys;
}

/**
 * Round 1 played by the rules with the demo's finger: the beam points where the finger is, the
 * lock builds while it holds the satellite, and the second satellite comes up after the first
 * one's link. Keyed every tenth of a second.
 */
function playDemo(finger: readonly FingerKey[]): { keys: DemoKey<DishView>[]; locks: number[]; heard: number | null; switched: number | null } {
  const L = DISH_LEVELS[0];
  const script: DemoScript<unknown> = { ms: DEMO_MS, keys: [], finger, steps: [] };
  const keys: DemoKey<DishView>[] = [];
  const locks: number[] = [];
  const dt = 1000 / 60;
  let aim = 0;
  let hold = 0;
  let touched = false;
  let linkUntil: number | null = null;
  let sat = DEMO_ONE;
  let born = 0;
  let heard: number | null = null;
  let switched: number | null = null;
  for (let frame = 0; frame * dt <= DEMO_MS; frame++) {
    const t = frame * dt;
    const f = fingerAt(script, t);
    if (f.down) {
      aim = aimAt(f);
      touched = true;
    }
    let now = false;
    if (linkUntil !== null && t >= linkUntil) {
      linkUntil = null;
      sat = DEMO_TWO;
      born = t;
      hold = 0;
      switched = t;
      now = true;
    }
    const p = satAt(sat, (t - born) / 1000);
    const won = locks.length >= L.sats;
    if (!won && linkUntil === null) {
      const on = f.down && onBeam(aim, p.x, p.y, L.beam);
      if (on && heard === null) heard = t;
      hold = stepLock(hold, dt, on);
      if (hold >= L.holdMs) {
        locks.push(t);
        if (locks.length < L.sats) linkUntil = t + LINK_MS;
      }
    }
    if (frame % 6 === 0 || now) {
      const done = locks.length >= L.sats;
      const linked = linkUntil !== null;
      const last = locks[locks.length - 1];
      const s: DishView['s'] = done ? 3 : linked ? 2 : touched ? 1 : 0;
      keys.push({
        at: Math.round(t),
        // the moment the second satellite comes up, it is there at once, not slid across
        ease: now ? 'step' : 'linear',
        view: {
          ...(frame === 0 ? { t: 0 } : {}),
          a: q(aim, 1),
          w: L.beam,
          x: q(p.x, 1),
          y: q(p.y, 1),
          // the first is there from the start of the loop; the second fades in as it comes up
          o: switched === null ? 1 : q(Math.min(1, (t - born) / FADE_MS), 2),
          d: f.down,
          h: done || linked ? 1 : q(Math.min(1, hold / L.holdMs), 2),
          n: locks.length,
          of: L.sats,
          s,
          fx: last !== undefined && t - last < HIT_MS ? [q(1 - (t - last) / HIT_MS, 2), 0] : [0, 0],
        },
      });
    }
  }
  return { keys, locks, heard, switched };
}

/**
 * The how-to: the finger presses the sky above the dish and drags the beam across onto the first
 * satellite, then follows it while the ring fills and it locks; the second comes up on the other
 * side, and the finger swings the beam over and holds that one too. Worked out by the rules.
 */
function demoAiming(): DemoScript<DishView> {
  const start = { x: PIVOT.x, y: 186 };
  const reach = 1_800;
  const opening: FingerKey[] = [
    { at: 0, ...start, hide: true },
    { at: 300, ...start, hide: false },
    { at: 800, ...start, down: true },
    { at: reach, ...towards(satAt(DEMO_ONE, reach / 1000)) },
  ];
  // First the finger follows the first satellite, to find when the second comes up.
  const first = playDemo([...opening, ...follow(DEMO_ONE, 0, reach + 400, DEMO_MS)]);
  const born = first.switched ?? (first.locks[0] ?? 3_300) + LINK_MS;
  // A moment to see it come up, then the beam swings over to it.
  const leave = Math.round(born) + 300;
  const arrive = leave + 800;
  const lead: FingerKey[] = [
    ...opening,
    ...follow(DEMO_ONE, 0, reach + 400, leave),
    { at: leave, ...towards(satAt(DEMO_ONE, leave / 1000)), ease: 'linear' },
    { at: arrive, ...towards(satAt(DEMO_TWO, (arrive - born) / 1000)) },
  ];
  // Then it follows the second until that one locks too, and lets go.
  const secondLock = playDemo([...lead, ...follow(DEMO_TWO, born, arrive + 400, DEMO_MS)]).locks[1] ?? DEMO_MS - 900;
  const lift = Math.round(secondLock + 300);
  const end = towards(satAt(DEMO_TWO, (lift - born) / 1000));
  const finger: FingerKey[] = [
    ...lead,
    ...follow(DEMO_TWO, born, arrive + 400, lift),
    { at: lift, ...end, down: false, ease: 'linear' },
    { at: Math.min(DEMO_MS, lift + 500), x: end.x + 20, y: end.y + 30, hide: true },
  ];
  const played = playDemo(finger);
  return {
    ms: DEMO_MS,
    keys: played.keys,
    finger,
    steps: [
      { at: 0, en: 'DRAG THE BEAM TO THE SATELLITE', no: 'DRA STRÅLEN TIL SATELLITTEN' },
      { at: Math.round(played.heard ?? 1_500), en: 'HOLD IT THERE UNTIL IT LOCKS', no: 'HOLD DEN DER TIL DEN LÅSES' },
      { at: Math.round(played.switched ?? born), en: 'TWO SATELLITES A ROUND', no: 'TO SATELLITTER HVER RUNDE' },
    ],
  };
}

export const game = thirtyGame<DishView>({
  Board,
  View,
  demo: demoAiming(),
  // Nothing to get wrong here: no mistake count under the board.
  mistakes: false,
  words: {
    en: {
      instruction: 'Drag the beam onto the satellite and hold it there until it locks. Two a round.',
      howMove: 'Drag your finger to point the beam at the satellite. Keep your finger down until it locks.',
    },
    no: {
      instruction: 'Dra strålen opp på satellitten og hold den der til den låses. To hver runde.',
      howMove: 'Dra fingeren for å sikte strålen mot satellitten. Hold fingeren nede til den låses.',
    },
  },
});
